import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HDate } from '@hebcal/hdate';
import LabeledInput from '@/pages/newSupported/components/LabeledInput';
import {
  civilDateFromHebrew,
  civilIsoFromLocalDate,
  formatHebrewYearGematriya,
  getHebrewDateParts,
  HEBREW_DAY_LETTERS,
  hebrewMonthName,
} from '@/helpers/hebrewDateHelper';
import { DOCUMENT_VALIDITY_MONTHS, isDocumentDateValid } from '@/helpers/supportDocumentDate';
import inputStyles from '@/pages/newSupported/components/components.module.css';
import styles from './DocumentDateField.module.scss';

type CalendarMode = 'gregorian' | 'hebrew';

interface DocumentDateFieldProps {
  /** i18n key of the label. */
  label: string;
  id: string;
  /** Civil YYYY-MM-DD, or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Wedding dates are often known by their Hebrew date - offer typing it that way. */
  allowHebrew?: boolean;
}

/** Tishrei first, as a Hebrew year runs. Adar I and Adar II only in a leap year. */
const monthsOfYear = (year: number): number[] => {
  const adar = HDate.isLeapYear(year) ? [12, 13] : [12];
  return [7, 8, 9, 10, 11, ...adar, 1, 2, 3, 4, 5, 6];
};

const formatGregorian = (iso: string): string => {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

const formatHebrewFull = (iso: string): string => {
  const parts = getHebrewDateParts(iso);
  return parts ? `${parts.boardLine} ${formatHebrewYearGematriya(parts.year)}` : '';
};

/**
 * SPR-10015: a date field for a support request's supporting document, with a note when the
 * date is outside the validity window. The note is advice, not a block: the office decides.
 */
const DocumentDateField = ({ label, id, value, onChange, allowHebrew = false }: DocumentDateFieldProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<CalendarMode>('gregorian');
  const today = civilIsoFromLocalDate(new Date());
  const currentHebrewYear = getHebrewDateParts(today)?.year ?? new HDate().getFullYear();

  // The Hebrew selects start from the stored date, or from this Hebrew year with nothing picked.
  const selected = value ? getHebrewDateParts(value) : null;
  const [hebrewYear, setHebrewYear] = useState<number>(selected?.year ?? currentHebrewYear);
  const [hebrewMonth, setHebrewMonth] = useState<number | null>(selected?.month ?? null);
  const [hebrewDay, setHebrewDay] = useState<number | null>(selected?.day ?? null);

  const yearOptions = useMemo(
    () => [currentHebrewYear - 1, currentHebrewYear, currentHebrewYear + 1, currentHebrewYear + 2],
    [currentHebrewYear]
  );

  const applyHebrew = (year: number, month: number | null, day: number | null) => {
    setHebrewYear(year);
    setHebrewMonth(month);
    setHebrewDay(day);
    if (month == null || day == null) {
      onChange(null);
      return;
    }
    // A 30th in a 29-day month is not a date - clamp to the month's last day.
    const lastDay = HDate.daysInMonth(month === 13 && !HDate.isLeapYear(year) ? 12 : month, year);
    const civil = civilDateFromHebrew(year, month, Math.min(day, lastDay));
    onChange(civil ? civilIsoFromLocalDate(civil) : null);
  };

  const switchMode = (next: CalendarMode) => {
    if (next === 'hebrew' && value) {
      const parts = getHebrewDateParts(value);
      if (parts) {
        setHebrewYear(parts.year);
        setHebrewMonth(parts.month);
        setHebrewDay(parts.day);
      }
    }
    setMode(next);
  };

  const isValid = value ? isDocumentDateValid(value, today) : null;
  const hintId = `${id}-hint`;

  return (
    <div className={styles.field}>
      {allowHebrew && (
        <div className={styles.modeSwitch} role="radiogroup" aria-label={t('documentDate.calendar')}>
          {(['gregorian', 'hebrew'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={mode === option}
              className={`body-sm-medium ${styles.modeOption} ${mode === option ? styles.modeOptionActive : ''}`}
              onClick={() => switchMode(option)}
            >
              {t(`documentDate.${option}`)}
            </button>
          ))}
        </div>
      )}

      {mode === 'gregorian' ? (
        <LabeledInput
          label={label}
          id={id}
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          required
        />
      ) : (
        <fieldset className={styles.hebrewFieldset}>
          <legend className="body-sm-regular text-(--font-heading) mb-1.5">{t(label)}</legend>
          <div className={styles.hebrewSelects}>
            <select
              id={id}
              aria-label={t('documentDate.day')}
              className={`${inputStyles.input} body-md-regular`}
              value={hebrewDay ?? ''}
              onChange={(e) => applyHebrew(hebrewYear, hebrewMonth, e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{t('documentDate.day')}</option>
              {HEBREW_DAY_LETTERS.slice(0, 30).map((letters, index) => (
                <option key={letters} value={index + 1}>
                  {letters}
                </option>
              ))}
            </select>
            <select
              aria-label={t('documentDate.month')}
              className={`${inputStyles.input} body-md-regular`}
              value={hebrewMonth ?? ''}
              onChange={(e) => applyHebrew(hebrewYear, e.target.value ? Number(e.target.value) : null, hebrewDay)}
            >
              <option value="">{t('documentDate.month')}</option>
              {monthsOfYear(hebrewYear).map((month) => (
                <option key={month} value={month}>
                  {hebrewMonthName(month, HDate.isLeapYear(hebrewYear))}
                </option>
              ))}
            </select>
            <select
              aria-label={t('documentDate.year')}
              className={`${inputStyles.input} body-md-regular`}
              value={hebrewYear}
              onChange={(e) => {
                const year = Number(e.target.value);
                // Adar II does not exist in a common year.
                const month = hebrewMonth === 13 && !HDate.isLeapYear(year) ? 12 : hebrewMonth;
                applyHebrew(year, month, hebrewDay);
              }}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {formatHebrewYearGematriya(year)}
                </option>
              ))}
            </select>
          </div>
        </fieldset>
      )}

      <div id={hintId} className={styles.hints} role="status" aria-live="polite">
        {value && (
          <span className="body-sm-regular text-(--font-body-2)">
            {mode === 'hebrew'
              ? t('documentDate.inGregorian', { date: formatGregorian(value) })
              : t('documentDate.inHebrew', { date: formatHebrewFull(value) })}
          </span>
        )}
        {isValid === false && (
          <span className={`body-sm-medium ${styles.invalidNote}`}>
            {t('documentDate.outOfRangeNote', { months: DOCUMENT_VALIDITY_MONTHS })}
          </span>
        )}
      </div>
    </div>
  );
};

export default DocumentDateField;
